import { io } from 'socket.io-client';

export const SENSOR_DATA_EVENT = 'sensor:data';
export const NOTIFICATION_NEW_EVENT = 'notification:new';
export const ALERT_CRITICAL_EVENT = 'alert:critical';
export const ACCESS_REQUEST_UPDATED_EVENT = 'access-request:updated';

const dispatchSocketEvent = (detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('shm:socket-event', { detail }));
};

let sharedSocket = null;
let sharedSocketUrl = null;
let sharedToken = null;
let subscriberCount = 0;
let releaseTimer = null;

export const unwrapSensorReading = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.reading && typeof payload.reading === 'object') return payload.reading;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  if (payload.sensorId || payload.sensorSerialNo || payload.timestamp) return payload;
  return null;
};

const getSharedSocket = (socketUrl, token) => {
  if (sharedSocket && sharedSocketUrl === socketUrl && sharedToken === token) {
    return sharedSocket;
  }
  if (sharedSocket) {
    sharedSocket.disconnect();
    sharedSocket = null;
  }
  sharedSocket = io(socketUrl, {
    withCredentials: true,
    path: '/socket.io',
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    auth: token ? { token } : undefined,
    query: token ? { token } : undefined,
    extraHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  sharedSocketUrl = socketUrl;
  sharedToken = token;
  return sharedSocket;
};

const releaseSharedSocket = () => {
  if (subscriberCount > 0 || !sharedSocket) return;
  sharedSocket.disconnect();
  sharedSocket = null;
  sharedSocketUrl = null;
  sharedToken = null;
};

const scheduleReleaseSharedSocket = () => {
  if (releaseTimer) clearTimeout(releaseTimer);
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    releaseSharedSocket();
  }, 300);
};

/**
 * Subscribe to shared Socket.IO connection (same host as API, path /socket.io).
 * Returns unsubscribe function. Multiple subscribers share one connection.
 */
export function subscribeSocket(socketUrl, token, handlers) {
  if (!token || !socketUrl) return () => {};

  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }

  subscriberCount += 1;
  const socket = getSharedSocket(socketUrl, token);

  const {
    onConnect,
    onDisconnect,
    onSensorData,
    onEvent,
  } = handlers || {};

  const handleConnect = () => onConnect?.();
  const handleDisconnect = () => onDisconnect?.();
  const handleSensorData = (payload) => {
    const reading = unwrapSensorReading(payload);
    if (!reading) return;
    onSensorData?.(reading);
    onEvent?.({ type: SENSOR_DATA_EVENT, reading });
  };
  const handleNotificationNew = (payload) => {
    const event = { type: NOTIFICATION_NEW_EVENT, ...payload };
    onEvent?.(event);
    dispatchSocketEvent(event);
  };
  const handleAlertCritical = (payload) => {
    const event = { type: ALERT_CRITICAL_EVENT, ...(payload && typeof payload === 'object' ? payload : {}) };
    onEvent?.(event);
    dispatchSocketEvent(event);
  };
  const handleAccessRequestUpdated = (payload) => {
    const event = {
      type: ACCESS_REQUEST_UPDATED_EVENT,
      accessRequest: payload?.accessRequest,
      companyId: payload?.companyId,
    };
    onEvent?.(event);
    dispatchSocketEvent(event);
  };

  socket.on('connect', handleConnect);
  socket.on('disconnect', handleDisconnect);
  socket.on(SENSOR_DATA_EVENT, handleSensorData);
  socket.on(NOTIFICATION_NEW_EVENT, handleNotificationNew);
  socket.on(ALERT_CRITICAL_EVENT, handleAlertCritical);
  socket.on(ACCESS_REQUEST_UPDATED_EVENT, handleAccessRequestUpdated);
  if (socket.connected) handleConnect();

  return () => {
    socket.off('connect', handleConnect);
    socket.off('disconnect', handleDisconnect);
    socket.off(SENSOR_DATA_EVENT, handleSensorData);
    socket.off(NOTIFICATION_NEW_EVENT, handleNotificationNew);
    socket.off(ALERT_CRITICAL_EVENT, handleAlertCritical);
    socket.off(ACCESS_REQUEST_UPDATED_EVENT, handleAccessRequestUpdated);
    subscriberCount = Math.max(0, subscriberCount - 1);
    scheduleReleaseSharedSocket();
  };
}
